import React, { type ComponentProps, useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl, type MessageValue } from 'react-intl';
import { Platform, StyleSheet, TouchableOpacity } from 'react-native';
import Animated, {
  CurvedTransition,
  FadeIn,
  SlideInRight,
  SlideOutRight,
} from 'react-native-reanimated';

import {
  BodyText,
  CloudinaryImage,
  type CloudinaryTransformConfig,
  Icon,
  theme,
} from '@sg/garnish';

import { BUNDLE_TRANSITION_DURATION } from '../../Bundle.constants';
import { type BundleSelectedProduct } from '../../Bundle.types';

// ─────────────────────────────────────────────────────────────────────────────

export const BundleSelectedItem = (props: Props) => {
  const { product, disabled, onRemove } = props;

  const { formatMessage } = useIntl();

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleRemove = useCallback(() => {
    onRemove(product.selectionId);
  }, [onRemove, product.selectionId]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const removeButtonStyles = [
    styles.removeButton,
    disabled ? styles.removeButtonDisabled : undefined,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Animated.View
      entering={ITEM_ENTERING}
      exiting={ITEM_EXITING}
      layout={ITEM_LAYOUT}
      style={styles.container}
    >
      <TouchableOpacity
        accessibilityLabel={formatMessage(messages.remove, { name: product.name })}
        accessibilityRole="button"
        activeOpacity={REMOVE_ACTIVE_OPACITY}
        disabled={disabled}
        style={removeButtonStyles}
        onPress={handleRemove}
      >
        <Icon
          name="IconMinus2"
          width={REMOVE_ICON_SIZE}
          height={REMOVE_ICON_SIZE}
          color={theme.colors.DARK_KALE}
        />
      </TouchableOpacity>

      <CloudinaryImage
        alt={product.name}
        baseUrl={product.imageUrl}
        config={IMAGE_TRANSFORM_CONFIG}
        contentFit="contain"
        style={styles.image}
      />

      <Animated.View layout={ITEM_LAYOUT} style={styles.details}>
        <BodyText sizeMatch={NAME_SIZE_MATCH} style={styles.name}>
          {product.name}
        </BodyText>

        {product.outOfStockIngredientNames ? (
          <Animated.View entering={OUT_OF_STOCK_ENTERING}>
            <BodyText sizeMatch={OUT_OF_STOCK_SIZE_MATCH} style={styles.outOfStock}>
              <FormattedMessage
                {...messages.outOfStock}
                values={{ ingredient_names: product.outOfStockIngredientNames }}
              />
            </BodyText>
          </Animated.View>
        ) : null}
      </Animated.View>
    </Animated.View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

// NOTE: Reanimated layout animations are unreliable on web, so rows only
//       animate on native.
const IS_ANIMATED = Platform.OS !== 'web';

const ITEM_ENTERING = IS_ANIMATED ? SlideInRight.duration(BUNDLE_TRANSITION_DURATION) : undefined;
const ITEM_EXITING = IS_ANIMATED ? SlideOutRight.duration(BUNDLE_TRANSITION_DURATION) : undefined;
const ITEM_LAYOUT = IS_ANIMATED ? CurvedTransition.duration(BUNDLE_TRANSITION_DURATION) : undefined;
const OUT_OF_STOCK_ENTERING = FadeIn.duration(BUNDLE_TRANSITION_DURATION);

const REMOVE_ACTIVE_OPACITY = 0.8;
const REMOVE_BUTTON_SIZE = 24;
const REMOVE_ICON_SIZE = 12;

const IMAGE_SIZE = 80;
const IMAGE_TRANSFORM_CONFIG: CloudinaryTransformConfig = [
  { crop: 'crop', height: 0.68, width: 0.38 },
  { width: IMAGE_SIZE },
];

const NAME_SIZE_MATCH: ComponentProps<typeof BodyText>['sizeMatch'] = ['14'];
const OUT_OF_STOCK_SIZE_MATCH: ComponentProps<typeof BodyText>['sizeMatch'] = ['12'];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['2'],
    paddingVertical: theme.spacing['1'],
    paddingHorizontal: theme.spacing['4'],
    borderRadius: theme.radius.large,
    backgroundColor: theme.colors.OPACITY.KALE.LIGHTEST,
  },
  removeButton: {
    alignItems: 'center',
    justifyContent: 'center',
    width: REMOVE_BUTTON_SIZE,
    height: REMOVE_BUTTON_SIZE,
    borderRadius: REMOVE_BUTTON_SIZE / 2,
    backgroundColor: theme.colors.WHITE,
  },
  removeButtonDisabled: {
    opacity: 0.5,
  },
  image: {
    width: IMAGE_SIZE,
    height: IMAGE_SIZE,
    borderRadius: theme.radius.large,
  },
  details: {
    flex: 1,
    gap: theme.spacing['1'],
    paddingVertical: theme.spacing['2'],
  },
  name: {
    color: theme.colors.DARK_KALE,
  },
  outOfStock: {
    color: theme.colors.CAUTION,
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly remove: { readonly name: MessageValue };
  readonly outOfStock: { readonly ingredient_names: MessageValue };
}>({
  remove: {
    defaultMessage: 'Remove {name}',
    description: 'Bundle | Included section | Remove selected product',
  },
  outOfStock: {
    defaultMessage: 'Out of stock: {ingredient_names}',
    description: 'Bundle | Included section | Default ingredients left off a selected product',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  product: BundleSelectedProduct;
  disabled?: boolean;
  onRemove: (selectionId: string) => void;
};
